import React, { useState } from "react";
import DashBoardLayout from "../components/ui/DashBoardLayout";
import PageLoader from "../components/ui/PageLoader";
import ShopProductView from "../components/shop/ShopProductView";
import ShopProductEdit from "../components/shop/ShopProductEdit";
import { FiEdit2, FiTrash2, FiEye, FiPlus } from "react-icons/fi";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";
import {
  useCreateProductMutation,
  useDeleteProductMutation,
  useGetProductsQuery,
  useUpdateProductMutation,
} from "apis/products";

const Shop = () => {
  const [viewProduct, setViewProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Queries
  const { data, isLoading, error } = useGetProductsQuery({ type: "shop" });

  // Mutations
  const createMutation = useCreateProductMutation();
  const updateMutation = useUpdateProductMutation();
  const deleteMutation = useDeleteProductMutation();

  const products = data || [];

  const handleCreate = async (formData) => {
    createMutation.mutate(formData, {
      onSuccess: () => {
        setIsAddModalOpen(false);
      }
    });
  };

  const handleUpdate = async (formData) => {
    formData.append("_id", editProduct._id);
    updateMutation.mutate(formData, {
      onSuccess: () => {
        setEditProduct(null);
      }
    });
  };

  const handleDelete = async (id) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setDeleteConfirm(null);
      }
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(price);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="container mx-auto flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">
            Failed to load shop products
          </p>
          <button
            onClick={() => window.location.reload()}
            className="black-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold mb-2">Shop Products</h1>
            <p className="text-darkgray">
              Manage your shop products and services
            </p>
          </div>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="black-button flex items-center gap-2"
          >
            <FiPlus className="w-5 h-5" />
            Add Product
          </Button>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-mediumgray rounded-lg">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2">No products yet</h3>
              <p className="text-darkgray mb-4">
                Get started by adding your first shop product
              </p>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="black-button"
              >
                Add Your First Product
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product._id}
                className="bg-white border border-mediumgray rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Product Image */}
                <div className="relative h-48 bg-gray-200">
                  {product.thumbnail ? (
                    <img
                      src={product.thumbnail}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <svg
                        className="w-16 h-16 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                  {/* category Badge */}
                  <span
                    className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-medium ${product.category === "payment"
                        ? "bg-green-500 text-white"
                        : "bg-blue-500 text-white"
                      }`}
                  >
                    {product.category === "payment" ? "Payment" : "Calendly"}
                  </span>
                </div>

                {/* Product Details */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-1">
                    {product.name}
                  </h3>
                  <p className="text-darkgray text-sm mb-3 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Price or Calendly indicator */}
                  {product.category === "payment" && product.price && (
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-green-600">
                        {formatPrice(product.price)}
                      </span>
                    </div>
                  )}

                  {product.category === "calendly" && (
                    <div className="mb-3 flex items-center gap-2 text-blue-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        Book Appointment
                      </span>
                    </div>
                  )}

                  {/* Video indicator */}
                  {product.video && (
                    <div className="mb-3 flex items-center gap-2 text-purple-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-xs">Has Video</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-mediumgray">
                    <button
                      onClick={() => setViewProduct(product)}
                      className="flex-1 outline-button flex items-center justify-center gap-2 text-sm"
                      title="View Details"
                    >
                      <FiEye className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => setEditProduct(product)}
                      className="flex-1 outline-button flex items-center justify-center gap-2 text-sm"
                      title="Edit Product"
                    >
                      <FiEdit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(product)}
                      className="px-3 outline-button text-red-600 hover:bg-red-50 border-red-300"
                      title="Delete Product"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewProduct && (
        <ShopProductView
          product={viewProduct}
          onClose={() => setViewProduct(null)}
        />
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <ShopProductEdit
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleCreate}
          isLoading={createMutation.isLoading}
        />
      )}

      {/* Edit Modal */}
      {editProduct && (
        <ShopProductEdit
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSubmit={handleUpdate}
          isLoading={updateMutation.isLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Shop Product"
        >
          <div className="flex items-center gap-3 mb-4">
            <div>
              <p className="text-sm text-darkgray">
                Are you sure you want to delete this product?
              </p>
            </div>
          </div>

          <div className="bg-lightergray border border-mediumgray rounded p-3 mb-4">
            <p className="font-medium">{deleteConfirm.name}</p>
            <p className="text-sm text-darkgray mt-1">
              {deleteConfirm.description?.substring(0, 100)}
              {deleteConfirm.description?.length > 100 ? "..." : ""}
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This will permanently delete the product
              and all associated files (thumbnail and video) from the system.
              This action cannot be undone.
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              onClick={() => setDeleteConfirm(null)}
              className="outline-button"
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              loading={deleteMutation.isLoading}
              onClick={() => handleDelete(deleteConfirm._id)}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? "Deleting..." : "Delete Product"}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Shop;
